if (!customElements.get('ice-logo-customizer')) {
  class IceLogoCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.initialize();
    }

    async initialize() {
      this.fileInput = this.querySelector('.ice-customizer__file-input');
      this.fileRow = this.querySelector('.ice-customizer__file-row');
      this.fileName = this.querySelector('.ice-customizer__file-name');
      this.removeButton = this.querySelector('.ice-customizer__remove');
      this.error = this.querySelector('.ice-customizer__error');
      this.viewer = this.querySelector('.ice-customizer__viewer');
      this.canvas = this.querySelector('.ice-customizer__canvas');
      this.controls = this.querySelector('.ice-customizer__controls');
      this.dragHint = this.querySelector('.ice-customizer__drag-hint');
      this.viewControls = this.querySelector('.ice-customizer__view-controls');
      this.viewButtons = [...this.querySelectorAll('[data-ice-view]')];
      this.threshold = this.querySelector('.ice-customizer__threshold');
      this.invert = this.querySelector('.ice-customizer__invert');
      this.styleProperty = this.querySelector('.ice-customizer__style-property');
      this.styleInputs = [...this.querySelectorAll('input[name^="IceStyle-"]')];
      this.fixedStyle = this.querySelector('.ice-customizer__fixed-style');
      this.scaleRange = this.querySelector('.ice-customizer__scale-range');
      this.scaleProperty = this.querySelector('.ice-customizer__scale-property');
      this.previewFileInput = this.querySelector('.ice-customizer__preview-file');
      this.previewAttachedProperty = this.querySelector('.ice-customizer__preview-attached-property');
      this.productForm = this.resolveProductForm();

      this.yaw = Math.PI / 4;
      this.pitch = -0.08;
      this.cameraView = '3d';
      this.dragStart = null;
      this.sourceImage = null;
      this.sourceSvgText = '';
      this.currentVectorSvg = '';
      this.currentVectorIsTrace = false;
      this.scaleFactor = Number(this.scaleRange?.value || 100) / 100;
      this.allowPreviewSubmit = false;
      this.submittingForm = null;
      this.logoCanvas = document.createElement('canvas');
      this.logoCanvas.width = 720;
      this.logoCanvas.height = 720;
      this.logoContext = this.logoCanvas.getContext('2d', { willReadFrequently: true });

      if (this.productForm) {
        this.productForm.enctype = 'multipart/form-data';
        let productFormId = this.productForm.getAttribute('id');
        if (!productFormId) {
          productFormId = `ice-product-form-${Date.now()}`;
          this.productForm.setAttribute('id', productFormId);
        }
        this.querySelectorAll('[name^="properties["]').forEach((field) => {
          field.setAttribute('form', productFormId);
        });
      }

      this.bindEvents();

      try {
        const [THREE, svgLoaderModule] = await Promise.all([
          import(this.dataset.threeModuleUrl),
          import(this.dataset.svgLoaderUrl)
        ]);
        this.THREE = THREE;
        this.SVGLoader = svgLoaderModule.SVGLoader;
        this.svgLoader = new this.SVGLoader();
        this.setupScene();
        this.resizeRenderer();
        this.render();
      } catch (error) {
        console.error('Ice preview could not start:', error);
        this.showError('The 3D preview could not start in this browser. Your artwork can still be attached to the order.');
      }
    }

    resolveProductForm() {
      const requestedId = this.dataset.productFormId;
      const requestedForm = requestedId ? document.getElementById(requestedId) : null;
      if (
        requestedForm instanceof HTMLFormElement &&
        requestedForm.matches('form[action*="/cart/add"]') &&
        requestedForm.closest('product-form')
      ) {
        return requestedForm;
      }

      const productScope =
        this.closest('product-info') ||
        this.closest('.shopify-section') ||
        document;
      return (
        productScope.querySelector('product-form form[action*="/cart/add"]') ||
        document.querySelector('product-form form[action*="/cart/add"]')
      );
    }

    bindEvents() {
      this.fileInput.addEventListener('change', () => {
        const file = this.fileInput.files && this.fileInput.files[0];
        if (file) this.loadFile(file);
      });

      this.removeButton.addEventListener('click', () => this.reset());
      this.threshold.addEventListener('input', () => this.rebuildLogo());
      this.invert.addEventListener('change', () => this.rebuildLogo());
      this.scaleRange?.addEventListener('input', () => {
        this.scaleFactor = Number(this.scaleRange.value) / 100;
        if (this.scaleProperty) this.scaleProperty.value = `${this.scaleRange.value}%`;
        this.buildRelief();
      });

      this.styleInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.styleProperty.value = input.value;
          this.buildRelief();
        });
      });

      this.viewButtons.forEach((button) => {
        button.addEventListener('click', () => this.setView(button.dataset.iceView));
      });

      ['dragenter', 'dragover'].forEach((eventName) => {
        this.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.classList.add('is-dragging');
        });
      });

      ['dragleave', 'drop'].forEach((eventName) => {
        this.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.classList.remove('is-dragging');
        });
      });

      this.addEventListener('drop', (event) => {
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (!file) return;
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.fileInput.files = transfer.files;
        this.loadFile(file);
      });

      this.viewer.addEventListener('pointerdown', (event) => {
        if (event.target.closest('.ice-customizer__view-controls')) return;
        if (!this.currentVectorSvg || !this.sceneReady) return;
        if (this.cameraView === 'top') {
          this.cameraView = '3d';
          this.updateViewButtons();
          this.frameCamera();
        }
        this.dragStart = { x: event.clientX, y: event.clientY, yaw: this.yaw, pitch: this.pitch };
        this.viewer.setPointerCapture(event.pointerId);
      });

      this.viewer.addEventListener('pointermove', (event) => {
        if (!this.dragStart) return;
        this.yaw = this.dragStart.yaw + (event.clientX - this.dragStart.x) * 0.012;
        this.pitch = this.dragStart.pitch + (event.clientY - this.dragStart.y) * 0.012;
        this.updateModelRotation();
        this.render();
      });

      const endDrag = () => {
        this.dragStart = null;
      };
      this.viewer.addEventListener('pointerup', endDrag);
      this.viewer.addEventListener('pointercancel', endDrag);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resizeRenderer());
        this.resizeObserver.observe(this.viewer);
      }

      this.documentSubmitHandler = (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"], form[id^="product-form-"]')) return;
        if (!form.contains(this.fileInput) && this.fileInput.getAttribute('form') !== form.getAttribute('id')) return;
        this.handleProductSubmit(event, form);
      };
      document.addEventListener('submit', this.documentSubmitHandler, true);
    }

    setupScene() {
      const THREE = this.THREE;
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance'
      });
      this.renderer.setClearColor(0x000000, 1);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.18;
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x000000);

      this.camera = new THREE.PerspectiveCamera(31, 1, 1, 600);

      this.scene.add(new THREE.HemisphereLight(0xe8f9ff, 0x071018, 2.25));

      const key = new THREE.DirectionalLight(0xffffff, 4.3);
      key.position.set(-70, 125, 80);
      key.castShadow = true;
      key.shadow.mapSize.set(1024, 1024);
      this.scene.add(key);

      const rim = new THREE.DirectionalLight(0x8cdfff, 3.5);
      rim.position.set(90, 70, -85);
      this.scene.add(rim);

      const front = new THREE.PointLight(0xd9f7ff, 75, 280, 2);
      front.position.set(0, 45, 115);
      this.scene.add(front);

      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(360, 360),
        new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 1, metalness: 0 })
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = -25.5;
      floor.receiveShadow = true;
      this.scene.add(floor);

      this.model = new THREE.Group();
      this.model.rotation.order = 'YXZ';
      this.scene.add(this.model);

      const cubeGeometry = new THREE.BoxGeometry(50, 50, 50, 18, 18, 18);
      const cubeMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xbcefff,
        transparent: true,
        opacity: 0.34,
        transmission: 0.6,
        thickness: 8,
        ior: 1.31,
        roughness: 0.12,
        metalness: 0,
        clearcoat: 0.35,
        clearcoatRoughness: 0.15,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      this.cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
      this.cube.castShadow = true;
      this.cube.receiveShadow = true;
      this.model.add(this.cube);

      const edgeMaterial = new THREE.LineBasicMaterial({
        color: 0xdff9ff,
        transparent: true,
        opacity: 0.42
      });
      this.cubeEdges = new THREE.LineSegments(new THREE.EdgesGeometry(cubeGeometry, 18), edgeMaterial);
      this.model.add(this.cubeEdges);

      this.updateModelRotation();
      this.sceneReady = true;
      if (this.currentVectorSvg) this.buildRelief();
    }

    async loadFile(file) {
      this.hideError();

      if (file.size > 10 * 1024 * 1024) {
        this.showError('Please choose a file smaller than 10 MB.');
        this.reset(false);
        return;
      }

      const isSvg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
      const supported =
        isSvg ||
        ['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
        /\.(png|jpe?g|webp)$/i.test(file.name);

      if (!supported) {
        this.showError('Please upload a PNG, JPG, WebP or SVG file.');
        this.reset(false);
        return;
      }

      try {
        this.sourceImage = null;
        this.sourceSvgText = '';
        this.currentVectorSvg = '';

        if (isSvg) {
          this.sourceSvgText = this.sanitizeSvg(await file.text());
        } else {
          const dataUrl = await this.readAsDataUrl(file);
          this.sourceImage = await this.loadImage(dataUrl);
        }

        this.fileName.textContent = file.name;
        this.fileRow.hidden = false;
        this.controls.hidden = false;
        this.dragHint.hidden = false;
        this.viewControls.hidden = false;
        this.classList.add('has-logo');
        this.cameraView = '3d';
        this.yaw = Math.PI / 4;
        this.pitch = -0.08;
        this.updateViewButtons();
        this.frameCamera();
        this.rebuildLogo();
      } catch (error) {
        console.error('Artwork could not be opened:', error);
        this.showError('That artwork could not be opened. Please export it again and retry.');
        this.reset(false);
      }
    }

    readAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('File read failed'));
        reader.readAsDataURL(file);
      });
    }

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Image decode failed'));
        image.src = url;
      });
    }

    sanitizeSvg(svgText) {
      const documentNode = new DOMParser().parseFromString(svgText, 'image/svg+xml');
      if (documentNode.querySelector('parsererror')) throw new Error('Invalid SVG');

      documentNode
        .querySelectorAll('script, foreignObject, iframe, object, embed, image')
        .forEach((node) => node.remove());
      documentNode.querySelectorAll('*').forEach((node) => {
        [...node.attributes].forEach((attribute) => {
          if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
          if (
            /^(href|xlink:href)$/i.test(attribute.name) &&
            !attribute.value.trim().startsWith('#')
          ) {
            node.removeAttribute(attribute.name);
          }
        });
      });
      return new XMLSerializer().serializeToString(documentNode.documentElement);
    }

    rebuildLogo() {
      if (!this.sceneReady) return;

      try {
        if (this.sourceSvgText) {
          this.currentVectorSvg = this.sourceSvgText;
          this.currentVectorIsTrace = false;
        } else if (this.sourceImage) {
          this.currentVectorSvg = this.traceRasterToSvg();
          this.currentVectorIsTrace = true;
        } else {
          return;
        }
        this.buildRelief();
      } catch (error) {
        console.error('Artwork conversion failed:', error);
        this.disposeRelief();
        this.showError(error.message || 'The artwork could not be converted into a 3D preview.');
        this.render();
      }
    }

    traceRasterToSvg() {
      if (!window.ImageTracer) throw new Error('The raster artwork converter did not load. Please refresh and try again.');

      const size = this.logoCanvas.width;
      const sourceRatio = this.sourceImage.naturalWidth / this.sourceImage.naturalHeight || 1;
      const margin = Math.round(size * 0.035);
      let drawWidth = size - margin * 2;
      let drawHeight = drawWidth / sourceRatio;
      if (drawHeight > size - margin * 2) {
        drawHeight = size - margin * 2;
        drawWidth = drawHeight * sourceRatio;
      }
      const drawX = Math.round((size - drawWidth) / 2);
      const drawY = Math.round((size - drawHeight) / 2);
      drawWidth = Math.max(1, Math.round(drawWidth));
      drawHeight = Math.max(1, Math.round(drawHeight));

      this.logoContext.clearRect(0, 0, size, size);
      this.logoContext.drawImage(this.sourceImage, drawX, drawY, drawWidth, drawHeight);
      const imageData = this.logoContext.getImageData(0, 0, size, size);
      const data = imageData.data;
      const background = this.estimateRasterBackground(data, size, {
        x: drawX,
        y: drawY,
        width: drawWidth,
        height: drawHeight
      });
      const threshold = Number(this.threshold.value);
      const invert = this.invert.checked;
      const mask = new Uint8Array(size * size);
      let minX = size;
      let minY = size;
      let maxX = -1;
      let maxY = -1;
      let foregroundCount = 0;

      for (let y = drawY; y < drawY + drawHeight; y += 1) {
        for (let x = drawX; x < drawX + drawWidth; x += 1) {
          const index = y * size + x;
          const offset = index * 4;
          const alpha = data[offset + 3] / 255;
          const difference = Math.sqrt(
            Math.pow(data[offset] - background.r, 2) +
              Math.pow(data[offset + 1] - background.g, 2) +
              Math.pow(data[offset + 2] - background.b, 2)
          );
          let foreground = background.transparent
            ? alpha > 0.2
            : alpha > 0.12 && difference > threshold;
          if (invert) {
            foreground = background.transparent
              ? alpha > 0.2 && this.pixelLuminance(data, offset) > 0.55
              : alpha > 0.12 && difference <= threshold;
          }

          if (!foreground) continue;
          mask[index] = 1;
          foregroundCount += 1;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }

      const drawArea = drawWidth * drawHeight;
      const coverage = foregroundCount / drawArea;
      if (maxX < minX || maxY < minY || coverage < 0.0006) {
        throw new Error('No clear logo shape was detected. Adjust Logo detail and try again.');
      }
      if (coverage > 0.76) {
        throw new Error('The image background is still being read as artwork. Increase Logo detail or use a transparent-background PNG.');
      }

      const cropWidth = maxX - minX + 1;
      const cropHeight = maxY - minY + 1;
      const padding = Math.max(4, Math.round(Math.max(cropWidth, cropHeight) * 0.02));
      const crop = {
        x: Math.max(drawX, minX - padding),
        y: Math.max(drawY, minY - padding),
        width: Math.min(drawX + drawWidth, maxX + padding + 1) - Math.max(drawX, minX - padding),
        height: Math.min(drawY + drawHeight, maxY + padding + 1) - Math.max(drawY, minY - padding)
      };
      const vectorLongest = 520;
      const vectorWidth = Math.max(8, Math.round((crop.width / Math.max(crop.width, crop.height)) * vectorLongest));
      const vectorHeight = Math.max(8, Math.round((crop.height / Math.max(crop.width, crop.height)) * vectorLongest));
      const vectorImage = new ImageData(vectorWidth, vectorHeight);

      for (let vy = 0; vy < vectorHeight; vy += 1) {
        for (let vx = 0; vx < vectorWidth; vx += 1) {
          const startX = Math.floor(crop.x + (vx / vectorWidth) * crop.width);
          const endX = Math.max(startX + 1, Math.ceil(crop.x + ((vx + 1) / vectorWidth) * crop.width));
          const startY = Math.floor(crop.y + (vy / vectorHeight) * crop.height);
          const endY = Math.max(startY + 1, Math.ceil(crop.y + ((vy + 1) / vectorHeight) * crop.height));
          let hits = 0;
          let total = 0;

          for (let sourceY = startY; sourceY < endY; sourceY += 1) {
            for (let sourceX = startX; sourceX < endX; sourceX += 1) {
              hits += mask[sourceY * size + sourceX];
              total += 1;
            }
          }

          const outputOffset = (vy * vectorWidth + vx) * 4;
          const black = total && hits / total >= 0.34;
          const value = black ? 0 : 255;
          vectorImage.data[outputOffset] = value;
          vectorImage.data[outputOffset + 1] = value;
          vectorImage.data[outputOffset + 2] = value;
          vectorImage.data[outputOffset + 3] = 255;
        }
      }

      return window.ImageTracer.imagedataToSVG(vectorImage, {
        ltres: 1,
        qtres: 1,
        pathomit: 3,
        rightangleenhance: false,
        colorsampling: 0,
        numberofcolors: 2,
        colorquantcycles: 1,
        mincolorratio: 0,
        layering: 0,
        strokewidth: 0,
        linefilter: true,
        scale: 1,
        roundcoords: 3,
        viewbox: true,
        desc: false,
        pal: [
          { r: 255, g: 255, b: 255, a: 255 },
          { r: 0, g: 0, b: 0, a: 255 }
        ]
      });
    }

    estimateRasterBackground(data, size, bounds) {
      const points = [];
      const steps = 32;
      const x0 = bounds.x;
      const y0 = bounds.y;
      const x1 = bounds.x + bounds.width - 1;
      const y1 = bounds.y + bounds.height - 1;

      for (let step = 0; step <= steps; step += 1) {
        const amount = step / steps;
        const x = Math.round(x0 + (x1 - x0) * amount);
        const y = Math.round(y0 + (y1 - y0) * amount);
        points.push([x, y0], [x, y1], [x0, y], [x1, y]);
      }

      const samples = points.map(([x, y]) => {
        const offset = (Math.max(0, Math.min(size - 1, y)) * size + Math.max(0, Math.min(size - 1, x))) * 4;
        return {
          r: data[offset],
          g: data[offset + 1],
          b: data[offset + 2],
          a: data[offset + 3] / 255
        };
      });
      const transparentSamples = samples.filter((sample) => sample.a < 0.12);
      if (transparentSamples.length > samples.length * 0.45) {
        return { r: 0, g: 0, b: 0, transparent: true };
      }

      const opaque = samples.filter((sample) => sample.a > 0.5);
      if (!opaque.length) return { r: 0, g: 0, b: 0, transparent: true };
      const median = (channel) => {
        const values = opaque.map((sample) => sample[channel]).sort((a, b) => a - b);
        return values[Math.floor(values.length / 2)];
      };
      return { r: median('r'), g: median('g'), b: median('b'), transparent: false };
    }

    pixelLuminance(data, offset) {
      return (0.2126 * data[offset] + 0.7152 * data[offset + 1] + 0.0722 * data[offset + 2]) / 255;
    }

    getStyle() {
      const selected = this.styleInputs.find((input) => input.checked);
      return selected ? selected.value : this.fixedStyle ? this.fixedStyle.value : 'Embossed';
    }

    buildRelief() {
      if (!this.sceneReady || !this.currentVectorSvg) return;
      const THREE = this.THREE;
      const parsed = this.svgLoader.parse(this.currentVectorSvg);
      const invert = this.invert.checked;
      const cutoff = Math.max(0.22, Math.min(0.92, 1 - Number(this.threshold.value) / 255));
      const filledShapes = [];
      const knockoutShapes = [];
      let vertexCount = 0;

      parsed.paths.forEach((path) => {
        const style = (path.userData && path.userData.style) || {};
        if (style.fill === 'none' || Number(style.fillOpacity) === 0) return;
        const luminance = 0.2126 * path.color.r + 0.7152 * path.color.g + 0.0722 * path.color.b;
        const include = this.currentVectorIsTrace ? luminance < 0.35 : invert ? luminance >= cutoff : luminance < cutoff;
        const shapes = this.SVGLoader.createShapes(path);
        if (include) {
          shapes.forEach((sourceShape) => {
            filledShapes.push(
              this.currentVectorIsTrace ? this.smoothTracedShape(sourceShape) : sourceShape
            );
          });
        } else if (!this.currentVectorIsTrace) {
          shapes.forEach((shape) => knockoutShapes.push(shape));
        }
      });

      if (!filledShapes.length) {
        throw new Error('No filled logo paths were found. If this SVG uses only strokes, convert the strokes to filled paths and upload it again.');
      }

      if (!this.currentVectorIsTrace && knockoutShapes.length) {
        knockoutShapes.forEach((knockout) => {
          const knockoutPoints = knockout.extractPoints(4).shape;
          const testPoint = knockoutPoints[0];
          if (!testPoint) return;
          const container = filledShapes
            .slice()
            .reverse()
            .find((shape) => this.pointInsidePolygon(testPoint, shape.extractPoints(4).shape));
          if (container) container.holes.push(knockout);
        });
      }

      const geometries = [];
      filledShapes.forEach((shape) => {
          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: 5,
            bevelEnabled: false,
            curveSegments: 24,
            steps: 1
          });
          vertexCount += geometry.getAttribute('position').count;
          if (vertexCount > 500000) {
            geometry.dispose();
            throw new Error('This artwork is too complex for an instant preview. Please simplify the SVG or upload a PNG.');
          }
          geometry.computeBoundingBox();
          geometries.push(geometry);
      });

      const bounds = new THREE.Box3();
      geometries.forEach((geometry) => bounds.union(geometry.boundingBox));
      const width = bounds.max.x - bounds.min.x;
      const height = bounds.max.y - bounds.min.y;
      const longest = Math.max(width, height);
      if (!Number.isFinite(longest) || longest <= 0) {
        geometries.forEach((geometry) => geometry.dispose());
        throw new Error('The logo has no usable filled area.');
      }

      const scale = 45 / longest;
      const centerX = (bounds.min.x + bounds.max.x) / 2;
      const centerY = (bounds.min.y + bounds.max.y) / 2;
      const embossed = this.getStyle() !== 'Debossed';
      const reliefMaterial = new THREE.MeshPhysicalMaterial({
        color: embossed ? 0xdaf8ff : 0x68aabd,
        transparent: true,
        opacity: embossed ? 0.92 : 0.7,
        transmission: embossed ? 0.16 : 0.02,
        thickness: 1.5,
        ior: 1.31,
        roughness: embossed ? 0.14 : 0.26,
        metalness: 0,
        clearcoat: 0.45,
        clearcoatRoughness: 0.14,
        side: THREE.DoubleSide,
        depthWrite: true
      });
      const nextRelief = new THREE.Group();

      geometries.forEach((geometry) => {
        geometry.translate(-centerX, -centerY, 0);
        // SVG Y coordinates run from top to bottom. Reverse that axis before
        // laying the artwork on the cube so its bottom faces the customer.
        // X remains unchanged, which prevents the logo from being mirrored.
        geometry.scale(scale * this.scaleFactor, -scale * this.scaleFactor, 1);
        geometry.rotateX(-Math.PI / 2);
        geometry.translate(0, embossed ? 25.03 : 20.03, 0);
        geometry.computeVertexNormals();
        const mesh = new THREE.Mesh(geometry, reliefMaterial);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.renderOrder = 3;
        nextRelief.add(mesh);
      });

      this.disposeRelief();
      this.relief = nextRelief;
      this.reliefMaterial = reliefMaterial;
      this.model.add(this.relief);
      this.hideError();
      this.updateModelRotation();
      this.invalidateAttachedPreview();
      this.render();
    }

    pointInsidePolygon(point, polygon) {
      let inside = false;
      for (let current = 0, previous = polygon.length - 1; current < polygon.length; previous = current++) {
        const a = polygon[current];
        const b = polygon[previous];
        const crosses =
          a.y > point.y !== b.y > point.y &&
          point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || Number.EPSILON) + a.x;
        if (crosses) inside = !inside;
      }
      return inside;
    }

    smoothTracedShape(sourceShape) {
      const THREE = this.THREE;
      const extracted = sourceShape.extractPoints(3);
      const contour = this.smoothClosedPoints(extracted.shape);
      const shape = new THREE.Shape(contour);
      shape.holes = extracted.holes
        .map((hole) => this.smoothClosedPoints(hole))
        .filter((hole) => hole.length >= 3)
        .map((hole) => new THREE.Path(hole));
      return shape;
    }

    smoothClosedPoints(sourcePoints) {
      const THREE = this.THREE;
      const points = sourcePoints.map((point) => new THREE.Vector2(point.x, point.y));
      if (
        points.length > 1 &&
        points[0].distanceToSquared(points[points.length - 1]) < 0.000001
      ) {
        points.pop();
      }
      if (points.length < 4) return points;

      let smoothed = [points[0]];
      for (let index = 1; index < points.length; index += 1) {
        if (points[index].distanceToSquared(smoothed[smoothed.length - 1]) >= 5.0625) {
          smoothed.push(points[index]);
        }
      }
      if (smoothed.length < 4) return points;

      for (let pass = 0; pass < 3; pass += 1) {
        const next = [];
        for (let index = 0; index < smoothed.length; index += 1) {
          const current = smoothed[index];
          const following = smoothed[(index + 1) % smoothed.length];
          next.push(
            new THREE.Vector2(
              current.x * 0.75 + following.x * 0.25,
              current.y * 0.75 + following.y * 0.25
            ),
            new THREE.Vector2(
              current.x * 0.25 + following.x * 0.75,
              current.y * 0.25 + following.y * 0.75
            )
          );
        }
        smoothed = next;
      }
      return smoothed;
    }

    disposeRelief() {
      if (!this.relief || !this.model) return;
      this.model.remove(this.relief);
      this.relief.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
      });
      if (this.reliefMaterial) this.reliefMaterial.dispose();
      this.relief = null;
      this.reliefMaterial = null;
    }

    updateModelRotation() {
      if (!this.model) return;
      this.model.rotation.y = this.yaw;
      this.model.rotation.x = this.pitch;
    }

    setView(view) {
      if (!this.camera || !this.model) return;
      if (view === 'top') {
        this.cameraView = 'top';
        this.yaw = 0;
        this.pitch = 0;
      } else {
        this.cameraView = '3d';
        this.yaw = Math.PI / 4;
        this.pitch = -0.08;
      }
      this.updateModelRotation();
      this.updateViewButtons();
      this.frameCamera();
      this.render();
    }

    updateViewButtons() {
      this.viewButtons.forEach((button) => {
        const active = button.dataset.iceView === this.cameraView;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    resizeRenderer() {
      if (!this.renderer || !this.camera) return;
      const rect = this.viewer.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.frameCamera();
      this.camera.updateProjectionMatrix();
      this.render();
    }

    frameCamera() {
      const THREE = this.THREE;
      const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
      const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * this.camera.aspect);
      const limitingFov = Math.min(verticalFov, horizontalFov);
      const modelRadius = 50;
      const distance = (modelRadius / Math.sin(limitingFov / 2)) * 1.16;
      const target = new THREE.Vector3(0, 1, 0);
      const viewingDirection =
        this.cameraView === 'top'
          ? new THREE.Vector3(0, 1, 0)
          : new THREE.Vector3(1.05, 0.92, 1.35).normalize();

      if (this.cameraView === 'top') {
        this.camera.up.set(0, 0, -1);
      } else {
        this.camera.up.set(0, 1, 0);
      }
      this.camera.position.copy(target).addScaledVector(viewingDirection, distance);
      this.camera.lookAt(target);
    }

    render() {
      if (this.renderer && this.scene && this.camera) this.renderer.render(this.scene, this.camera);
    }

    handleProductSubmit(event, form) {
      if (this.allowPreviewSubmit && this.submittingForm === form) {
        this.allowPreviewSubmit = false;
        this.submittingForm = null;
        return;
      }
      if (!this.currentVectorSvg || !this.relief) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Upload your logo before adding this item to the cart.');
        this.fileInput.focus();
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      this.attachTopPreview(form).then((attached) => {
        if (!attached) return;
        this.allowPreviewSubmit = true;
        this.submittingForm = form;
        if (form.requestSubmit) {
          submitter && submitter.isConnected ? form.requestSubmit(submitter) : form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }

    attachTopPreview(form) {
      if (!this.previewFileInput || typeof DataTransfer === 'undefined') {
        this.showError('This browser cannot attach the top-view preview. Please use a current browser.');
        return Promise.resolve(false);
      }
      const originalFile = this.fileInput?.files?.[0];
      if (!(originalFile instanceof File) || originalFile.size <= 0) {
        this.showError('Upload your original artwork before adding this item to the cart.');
        return Promise.resolve(false);
      }
      const previous = { cameraView: this.cameraView, yaw: this.yaw, pitch: this.pitch };
      this.cameraView = 'top';
      this.yaw = 0;
      this.pitch = 0;
      this.updateModelRotation();
      this.updateViewButtons();
      this.frameCamera();
      this.render();
      return new Promise((resolve) => {
        this.canvas.toBlob((blob) => {
          this.cameraView = previous.cameraView;
          this.yaw = previous.yaw;
          this.pitch = previous.pitch;
          this.updateModelRotation();
          this.updateViewButtons();
          this.frameCamera();
          this.render();
          if (!blob) {
            this.showError('The top-view preview could not be created. Please try again.');
            resolve(false);
            return;
          }
          const transfer = new DataTransfer();
          transfer.items.add(new File([blob], `ice-logo-top-view-${Date.now()}.png`, { type: 'image/png' }));
          this.previewFileInput.files = transfer.files;
          this.fileInput.removeAttribute('form');
          if (!form.contains(this.fileInput)) form.appendChild(this.fileInput);
          this.previewFileInput.removeAttribute('form');
          if (!form.contains(this.previewFileInput)) form.appendChild(this.previewFileInput);
          const submittedForm = new FormData(form);
          const submittedPreview = submittedForm.get('properties[Top-view preview]');
          const submittedOriginal = submittedForm.get('properties[Original artwork]');
          const validPreview = submittedPreview instanceof File && submittedPreview.size > 0;
          const validOriginal = submittedOriginal instanceof File && submittedOriginal.size > 0;
          const valid = validPreview && validOriginal;
          if (valid) {
            if (this.previewAttachedProperty) this.previewAttachedProperty.value = 'Yes';
            this.hideError();
          } else {
            this.previewFileInput.value = '';
            this.showError('The top-view preview and original artwork could not both be attached. Please try again.');
          }
          resolve(valid);
        }, 'image/png');
      });
    }

    invalidateAttachedPreview() {
      if (this.previewFileInput?.files?.length) this.previewFileInput.value = '';
      if (this.previewAttachedProperty) this.previewAttachedProperty.value = 'No';
    }

    reset(clearError = true) {
      this.fileInput.value = '';
      this.sourceImage = null;
      this.sourceSvgText = '';
      this.currentVectorSvg = '';
      this.currentVectorIsTrace = false;
      this.invalidateAttachedPreview();
      this.disposeRelief();
      this.fileRow.hidden = true;
      this.controls.hidden = true;
      this.dragHint.hidden = true;
      this.viewControls.hidden = true;
      this.cameraView = '3d';
      this.yaw = Math.PI / 4;
      this.pitch = -0.08;
      this.updateModelRotation();
      this.updateViewButtons();
      this.frameCamera();
      this.classList.remove('has-logo');
      if (clearError) this.hideError();
      this.render();
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      this.error.textContent = '';
      this.error.hidden = true;
    }

    disconnectedCallback() {
      if (this.documentSubmitHandler) document.removeEventListener('submit', this.documentSubmitHandler, true);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.disposeRelief();
      if (this.renderer) this.renderer.dispose();
    }
  }

  customElements.define('ice-logo-customizer', IceLogoCustomizer);
}
